import { useEffect, useState, useRef, useCallback } from "react";
import { useAuth } from "./auth";
import Sidebar from "./components/Sidebar";
import { Toast } from "./components/ui";
import ErrorBoundary from "./components/ErrorBoundary";
import Login from "./pages/Login";
import AdminOverview from "./pages/admin/Overview";
import AdminStudents from "./pages/admin/Students";
import AdminNotifications from "./pages/admin/Notifications";
import AdminUsers from "./pages/admin/Users";
import AdminCourses from "./pages/admin/Courses";
import StudentOverview from "./pages/student/Overview";
import StudentCourse from "./pages/student/Course";
import StudentNotifications from "./pages/student/Notifications";

function useToast() {
  const [msg, setMsg] = useState(null);
  const toast = (m) => { setMsg(m); setTimeout(() => setMsg(null), 2400); };
  return { msg, toast };
}

// Sync top-level page with the URL hash so the browser Back/Forward buttons
// move between sidebar pages.  Sub-view state (e.g. which course is being
// edited, which student detail is open) is stored in history.state.sub and
// dispatched via a custom "subviewpop" event so child components can restore.
function usePageNav(keys, fallback) {
  const initial = keys.includes(window.location.hash.replace("#", "")) ? window.location.hash.replace("#", "") : fallback;
  const [page, setPage] = useState(initial);
  useEffect(() => {
    if (window.location.hash.replace("#", "") !== page) {
      window.history.replaceState({ page, sub: null }, "", "#" + page);
    }
    const onPop = (e) => {
      const h = window.location.hash.replace("#", "");
      setPage(keys.includes(h) ? h : fallback);
      // Notify sub-view hooks about restored state
      window.dispatchEvent(new CustomEvent("subviewpop", { detail: e.state?.sub || null }));
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  const go = (p) => {
    setPage(p);
    if (window.location.hash.replace("#", "") !== p) window.history.pushState({ page: p, sub: null }, "", "#" + p);
  };
  return [page, go];
}

// Hook for sub-view navigation within a page.  Pushes/pops history entries
// so the browser back button returns from e.g. course editor → course list.
// `encode` turns the view state into a serialisable object; `decode` does the
// reverse.  The hook returns [state, setState] where setState also pushes a
// history entry (unless `replace` option is set).
function useSubView(initial, { encode = (v) => v, decode = (v) => v } = {}) {
  const [view, _setView] = useState(() => {
    // On mount, check if history.state already has a sub-view (page refresh)
    const saved = window.history.state?.sub;
    return saved ? decode(saved) : initial;
  });
  const viewRef = useRef(view);
  viewRef.current = view;

  useEffect(() => {
    const onSubPop = (e) => {
      const sub = e.detail;
      _setView(sub ? decode(sub) : initial);
    };
    window.addEventListener("subviewpop", onSubPop);
    return () => window.removeEventListener("subviewpop", onSubPop);
  }, []);

  const setView = useCallback((v, { replace = false } = {}) => {
    _setView(v);
    const encoded = encode(v);
    const hash = window.location.hash;
    const page = window.history.state?.page || hash.replace("#", "");
    if (replace) {
      window.history.replaceState({ page, sub: encoded }, "", hash);
    } else {
      window.history.pushState({ page, sub: encoded }, "", hash);
    }
  }, [encode]);

  return [view, setView];
}

function Shell({ items, page, go, children }) {
  const { msg, toast } = useToast();
  const [collapsed, setCollapsed] = useState(false);
  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar items={items} active={page} onSelect={go} collapsed={collapsed} onToggleCollapse={() => setCollapsed((c) => !c)} />
      <main className={`flex-1 p-6 sm:p-8 ${collapsed ? "" : "max-w-6xl"}`}>
        <ErrorBoundary resetKey={page}>{children(toast, { collapsed, setCollapsed })}</ErrorBoundary>
      </main>
      <Toast msg={msg} />
    </div>
  );
}

function AdminApp() {
  const { user } = useAuth();
  const can = (key) => {
    if (!user || user.role !== "admin") return false;
    if (user.privileges === null || user.privileges === undefined) return true; // super admin
    return user.privileges.includes(key);
  };
  const isSuperAdmin = user?.privileges === null || user?.privileges === undefined;
  const allItems = [
    { key: "overview", label: "Overview", icon: "dashboard", show: can("overview_visibility") },
    { key: "students", label: "Students", icon: "users", show: can("all_view") || can("add_students") },
    { key: "courses", label: "Courses", icon: "book", show: can("course_view") || can("edit_curriculum") || can("duplicate_course") || can("delete_course") },
    { key: "notifications", label: "Notifications", icon: "bell", show: can("notification_section") },
    { key: "users", label: "Users", icon: "shield", show: isSuperAdmin },
  ];
  const items = allItems.filter((i) => i.show);
  const [page, go] = usePageNav(items.map((i) => i.key), items[0]?.key || "overview");
  const [navKey, setNavKey] = useState(0);
  const [focusStudent, setFocusStudent] = useState(null);
  const openStudent = (id) => { setFocusStudent(id); go("students"); };
  const navigate = (p) => { go(p); setNavKey(k => k + 1); };
  return (
    <Shell items={items} page={page} go={navigate}>
      {(toast) => (<>
        {page === "overview" && <AdminOverview />}
        {page === "students" && <AdminStudents key={navKey} toast={toast} focusStudent={focusStudent} onFocusHandled={() => setFocusStudent(null)} can={can} />}
        {page === "courses" && <AdminCourses key={navKey} toast={toast} can={can} />}
        {page === "notifications" && <AdminNotifications toast={toast} onOpenStudent={openStudent} />}
        {page === "users" && isSuperAdmin && <AdminUsers toast={toast} />}
      </>)}
    </Shell>
  );
}

function StudentApp() {
  const items = [
    { key: "overview", label: "Overview", icon: "dashboard" },
    { key: "course", label: "Course Modules", icon: "book" },
    { key: "notifications", label: "Notifications", icon: "bell" },
  ];
  const [page, go] = usePageNav(items.map((i) => i.key), "overview");
  return (
    <Shell items={items} page={page} go={go}>
      {(toast, sidebar) => (<>
        {page === "overview" && <StudentOverview toast={toast} />}
        {page === "course" && <StudentCourse toast={toast} sidebar={sidebar} />}
        {page === "notifications" && <StudentNotifications />}
      </>)}
    </Shell>
  );
}

export { useSubView };

export default function App() {
  const { user, ready, hasToken, retryAuth } = useAuth();
  const [retrying, setRetrying] = useState(false);
  if (!ready) return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading…</div>;
  // Token exists but user failed to load (network error during refresh) — offer retry
  if (!user && hasToken) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-slate-500 gap-3">
        <div>Reconnecting…</div>
        <button onClick={() => { setRetrying(true); retryAuth().finally(() => setRetrying(false)); }}
          disabled={retrying}
          className="px-4 py-2 rounded-lg bg-slate-200 hover:bg-slate-300 text-sm">
          {retrying ? "Connecting…" : "Retry"}
        </button>
      </div>
    );
  }
  if (!user) return <Login />;
  return user.role === "admin" ? <AdminApp /> : <StudentApp />;
}
